import { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import {
  fetchClientEditOptions,
  updateClient,
  deleteClient,
  sendClientCredentials,
  type ClientDetail,
  type ClientEditOptions,
} from '@/api/clients';
import { useTooltip } from '@/hooks/useTooltip';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { useSession } from '@/hooks/useSession';
import { Icon } from '@/components/Icon';
import { InfoRow } from '@/components/InfoRow';
import { showToast } from '@/lib/toast';
import { STATUS_TONE, STATUS_LABEL, TARIFF_LABEL, TYPE_LABEL, formatDate, initials } from '@/lib/clientFormat';
import { buildEditState, buildUpdatePayload, validateEditState, REQUIRED_FIELD_LABELS, type GeneralEditState } from './generalEditState';
import { GeneralEditForm } from './GeneralEditForm';
import { BalancePanel } from './BalancePanel';
import { LibraryTestsPanel } from './LibraryTestsPanel';
import { PricesPanel } from './PricesPanel';
import { UsersPanel } from './UsersPanel';
import { HistoryPanel } from './HistoryPanel';

type TabKey = 'general' | 'balance' | 'tests' | 'prices' | 'users' | 'history';

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'general', label: 'Общее' },
  { key: 'balance', label: 'Баланс' },
  { key: 'tests', label: 'Тесты' },
  { key: 'prices', label: 'Цены' },
  { key: 'users', label: 'Пользователи' },
  { key: 'history', label: 'История изменений' },
];

/**
 * Карточка клиента – JSON-эквивалент того, что Orchid показывает на
 * CustomerEditScreen (см. CustomerDetailResource на бэке). Только просмотр:
 * «Редактировать» – стаб (см. докблок кнопки ниже), переход на лицензии MT1 –
 * тоже без реального вызова. Библиотечные тесты (вкладка «Тесты») – вкл/выкл
 * реально активирует тест в MT1 (LibraryTestsPanel). Цены (вкладка «Цены») и
 * баланс (выдать/списать) – реальные операции, см. PricesPanel/BalancePanel.
 */
export function ClientDetailView({ data: initialData }: { data: ClientDetail }) {
  const navigate = useNavigate();
  const location = useLocation() as {
    pathname: string;
    state: { autoEdit?: boolean; openTab?: TabKey } | null;
  };
  // Локальная копия — обновляется целиком после успешного сохранения
  // (updateClient() возвращает свежий ClientDetail), чтобы тариф/баланс/
  // бейдж MT1/список кураторов и т.д. сразу отразили результат, без
  // отдельного повторного fetchClient(). key={data.id} на ClientDetailPage
  // гарантирует свежий mount (и сброс этого состояния) при смене клиента.
  const [data, setData] = useState(initialData);
  // При тарифе "лицензии" тестов/цен не существует – тот же скоуп, что
  // CustomerTestScreen::query() проверяет (getTariffType() === 'license'
  // → тестов нет вообще, у лицензионных кабинетов доступ к MT1 через саму
  // лицензию, библиотека тестов клиенту не назначается).
  const licenseMode = data.tariffType === 'license';
  const tabs = ALL_TABS.filter((t) => {
    if (licenseMode && (t.key === 'tests' || t.key === 'prices')) return false;
    if (t.key === 'users' && !data.hasUsers) return false;
    return true;
  });
  const [activeTab, setActiveTab] = useState<TabKey>('general');
  // Баланс — состояние родителя, а не BalancePanel: вкладки монтируются
  // условно ({activeTab === 'balance' && ...}), при переключении туда-обратно
  // BalancePanel размонтируется и потерял бы результат выдачи/списания, если
  // бы хранил его сам (так и было — после смены таба показывалось старое
  // значение с первой загрузки страницы).
  const [balance, setBalance] = useState(data.balance);
  const tone = STATUS_TONE[data.status] ?? 'neutral';
  const statusLabel = STATUS_LABEL[data.status] ?? data.status;

  const [editing, setEditing] = useState(false);
  const [editState, setEditState] = useState<GeneralEditState | null>(null);
  const [editOptions, setEditOptions] = useState<ClientEditOptions | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Показываем красную подсветку конкретных полей только после первой
  // неудачной попытки сохранить — не заранее, пока пользователь ещё
  // заполняет форму.
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingCredentials, setSendingCredentials] = useState(false);
  const { confirm, alert: alertDialog, dialog: confirmDialog } = useConfirmDialog();
  const { refreshPortfolioSize } = useSession();
  const deleteTooltip = useTooltip<HTMLButtonElement>('Удалить клиента');
  const missingFields = editState ? validateEditState(editState) : [];

  const startEditing = () => {
    if (activeTab !== 'general') setActiveTab('general');
    setEditState(buildEditState(data));
    setSaveError(null);
    setAttemptedSave(false);
    setEditing(true);
    if (!editOptions) {
      fetchClientEditOptions()
        .then(setEditOptions)
        .catch(() => showToast('Не удалось загрузить справочники для редактирования.'));
    }
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditState(null);
    setSaveError(null);
    setAttemptedSave(false);
  };

  // Ссылки из других разделов ведут сюда с намерением: state.autoEdit —
  // «Редактировать» из меню строки списка и «Продлить» из дашборда (сразу
  // открываем "Общее" в режиме правки), state.openTab — «Подключить тесты»
  // из дашборда (сразу нужная вкладка). Без этого пользователю пришлось бы
  // повторять на карточке то, что он уже нажал.
  // replace, чтобы флаг не остался в истории и не сработал повторно на
  // "назад"/обновление страницы.
  useEffect(() => {
    const { autoEdit, openTab } = location.state ?? {};
    if (!autoEdit && !openTab) return;

    if (autoEdit) startEditing();
    // Вкладка могла быть скрыта (у лицензионного тарифа нет "Тесты"/"Цены") —
    // тогда просто остаёмся на "Общем".
    if (openTab && tabs.some((t) => t.key === openTab)) setActiveTab(openTab);

    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при монтировании, см. комментарий выше
  }, []);

  const handleTabClick = async (key: TabKey) => {
    if (editing && key !== 'general') {
      const ok = await confirm('Есть несохранённые изменения. Уйти без сохранения?', {
        confirmLabel: 'Уйти',
        cancelLabel: 'Остаться',
      });
      if (!ok) return;
      cancelEditing();
    }
    setActiveTab(key);
  };

  const saveEditing = async () => {
    if (!editState) return;
    if (missingFields.length > 0) {
      setAttemptedSave(true);
      setSaveError(
        `Заполните обязательные поля: ${missingFields.map((k) => REQUIRED_FIELD_LABELS[k]).join(', ')}.`,
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const fresh = await updateClient(data.id, buildUpdatePayload(editState));
      setData(fresh);
      setBalance(fresh.balance);
      setEditing(false);
      setEditState(null);
      showToast('Клиент сохранён');
    } catch {
      // Диалог, а не только текст в форме — ошибку сохранения (в отличие от
      // непройденной валидации, где нагляднее подсветка конкретных полей)
      // легко пропустить, если просто дописать в баннер над формой.
      await alertDialog('Не удалось сохранить изменения. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm(
      `Удалить клиента «${data.company ?? data.mt1Name}»? Это действие необратимо.`,
      { confirmLabel: 'Удалить', danger: true },
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteClient(data.id);
      showToast('Клиент удалён');
      refreshPortfolioSize();
      navigate('/clients');
    } catch {
      showToast('Не удалось удалить клиента.');
      setDeleting(false);
    }
  };

  const handleSendCredentials = async () => {
    setSendingCredentials(true);
    try {
      await sendClientCredentials(data.id);
      showToast('Учётные данные отправлены');
    } catch (err) {
      // Рейт-лимит (429) с бэка приходит с конкретным текстом ("Письмо было уже
      // отправлено N минут назад...") — показываем его как есть, не глушим generic-сообщением.
      const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      showToast(message || 'Не удалось отправить учётные данные.');
    } finally {
      setSendingCredentials(false);
    }
  };

  return (
    <section className="detail">
      <Link to="/clients" className="detail__back">
        <Icon name="chevronLeft" size={14} /> Все клиенты
      </Link>

      <header className="detail__header">
        <div className="detail__hero">
          <div className="detail__mark">{initials(data.company)}</div>
          <div>
            <h1>{data.company ?? 'Без названия'}</h1>
            <div className="detail__meta">
              <span className={`badge badge--${tone}`}>
                <span className="badge__dot" />
                {statusLabel}
              </span>
              {data.adminContact.name && (
                <>
                  <span className="detail__meta-sep">·</span>
                  <span>{data.adminContact.name}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="detail__header-actions">
          {/* Сохранить/Отмена – только на вкладке "Общее" (это единственная
              редактируемая вкладка); смена вкладки во время редактирования
              сама выходит из режима редактирования, см. handleTabClick(). */}
          {editing && activeTab === 'general' ? (
            <>
              <button type="button" className="btn btn--secondary" onClick={cancelEditing} disabled={saving}>
                Отмена
              </button>
              <button type="button" className="btn btn--primary" onClick={saveEditing} disabled={saving}>
                <Icon name="check" size={15} /> {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary" onClick={startEditing}>
              <Icon name="pencil" size={15} /> Редактировать
            </button>
          )}
          {!editing && data.canRemove && (
            <button
              ref={deleteTooltip.ref}
              type="button"
              className="btn btn--icon-danger"
              onClick={handleDelete}
              disabled={deleting}
              aria-label="Удалить клиента"
              {...deleteTooltip.handlers}
            >
              <Icon name="trash" size={15} />
              {deleteTooltip.node}
            </button>
          )}
        </div>
      </header>

      {/* Переключение между разделами карточки – вкладками внутри одной
          карточки (как в референсе), а не всё сразу длинной прокруткой. */}
      <div className="card tabs">
        <div className="tabs__list" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`tabs__item${activeTab === tab.key ? ' tabs__item--active' : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tabs__panel" role="tabpanel">
          {activeTab === 'general' &&
            (editing && editState ? (
              <GeneralEditForm
                data={data}
                state={editState}
                onChange={setEditState}
                editOptions={editOptions}
                error={saveError}
                fieldErrors={attemptedSave ? missingFields : []}
                saving={saving}
              />
            ) : (
              <div className="detail__grid-2">
                <div className="card">
                  <h2 className="detail__subtitle">Информация о компании</h2>
                  <dl className="info-list">
                    <InfoRow label="Наименование компании" value={data.company} />
                    <div className="info-list__row">
                      <dt>Статус аккаунта</dt>
                      <dd>
                        <span className={`badge badge--${tone}`}>
                          <span className="badge__dot" />
                          {statusLabel}
                        </span>
                      </dd>
                    </div>
                    <div className="info-list__row">
                      <dt>Кабинет в Maintest 5i</dt>
                      <dd className="info-list__value-row">
                        <span>{data.mt1Name || '–'}</span>
                        <span className={`badge badge--${data.isMt1HallAllowed ? 'good' : 'neutral'}`}>
                          {data.isMt1HallAllowed ? 'Вход разрешён' : 'Вход не разрешён'}
                        </span>
                      </dd>
                    </div>
                    <InfoRow
                      label="Тип"
                      value={data.type ? (TYPE_LABEL[data.type] ?? data.type) : null}
                    />
                    <InfoRow
                      label="Тип тарификации"
                      value={TARIFF_LABEL[data.tariffType] ?? data.tariffType}
                    />
                    <InfoRow
                      label="Кураторы"
                      value={
                        data.managers.length ? data.managers.map((m) => m.name).join(', ') : null
                      }
                    />
                    <InfoRow
                      label="Дата отключения"
                      value={data.validity ? formatDate(data.validity) : 'Бессрочно'}
                    />
                  </dl>

                  {data.notes && (
                    <>
                      <div className="detail__divider" />
                      <h2 className="detail__subtitle detail__subtitle--sm">Заметки</h2>
                      <p className="detail__notes">{data.notes}</p>
                    </>
                  )}
                </div>

                <div className="stack">
                  <div className="card">
                    <div className="detail__subtitle-row">
                      <h2 className="detail__subtitle">Основной контакт</h2>
                      {data.canSendCredentials && (
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={handleSendCredentials}
                          disabled={sendingCredentials}
                        >
                          {sendingCredentials ? 'Отправка…' : 'Отправить учётные данные'}
                        </button>
                      )}
                    </div>
                    <div className="contact-row">
                      <Icon name="user" size={16} />
                      <span>
                        {data.adminContact.name ?? '–'}
                        {data.adminContact.position && (
                          <span className="cell-muted"> · {data.adminContact.position}</span>
                        )}
                      </span>
                    </div>
                    {data.adminContact.email && (
                      <div className="contact-row">
                        <Icon name="mail" size={16} />
                        <a href={`mailto:${data.adminContact.email}`}>{data.adminContact.email}</a>
                      </div>
                    )}
                    {data.adminContact.phone && (
                      <div className="contact-row">
                        <Icon name="phone" size={16} />
                        <span>{data.adminContact.phone}</span>
                      </div>
                    )}
                  </div>

                  <div className="card">
                    <h2 className="detail__subtitle">Персональные данные</h2>
                    <dl className="info-list">
                      <InfoRow label="Шаблон соглашения" value={data.personalData.operatorTemplate} />
                      <InfoRow label="ОГРН" value={data.personalData.ogrn} />
                      <InfoRow label="Юридический адрес" value={data.personalData.address} />
                    </dl>
                  </div>
                </div>
              </div>
            ))}

          {activeTab === 'balance' && (
            <BalancePanel
              balance={balance}
              onBalanceChange={setBalance}
              accountId={data.id}
              licensesDisabled={data.status === 'disable'}
            />
          )}

          {activeTab === 'tests' && <LibraryTestsPanel accountId={data.id} />}

          {activeTab === 'prices' && <PricesPanel accountId={data.id} />}

          {activeTab === 'users' && <UsersPanel accountId={data.id} />}

          {activeTab === 'history' && <HistoryPanel accountId={data.id} />}
        </div>
      </div>

      {confirmDialog}
    </section>
  );
}
